/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { ApiResponse, FormStore, GlobalStore } from "@roc/feature-app-core";
import { action, computed, flow, makeObservable, observable, reaction } from "mobx";
import { SubjectPropertyInformationStore } from "./subjectPropertyInformationStore";
import { ValuationFormStore } from "./valuationFormStore";
import { NeighborhoodFormStore } from "./neigborhoodFormStore";
import { InternalValuationReviewService } from "../services/internalValuationReviewService";
import { DateFormat, formatDate, GENERIC_ERROR_MESSAGE, isLocalHost, LoanSubType } from "@roc/feature-utils";
import { ValuationReviewReportInternalService } from "apps/roc-internal-portal/src/app/modules/valuationReview/services/valuationReviewInternalService";
import { convertStringNumberToNumber, formatNumberField, getFullAddress, hasValue, toMMDDYYYY } from "../utils/utils";
import { PropertyCompFormStore } from "./propertyCompFormStore";
import { getCachedPropertyCompImage, isRemoteImage, loadPropertyCompImage, loadSubjectImage } from "../utils/propertyCompImage";
import { DocumentService } from "@roc/feature-documents";
import { remainingReno, totalCapex, totalRemainingReno } from "../utils/scopeOfWork";

const form = {
  fields: {

  },
  meta: {
    isValid: false,
    error: null,
  },
};

//Neighborhood values that come from DS but the analyst can override. A saved (non null) value wins over DS,
//a null one means "use the DS value".
const NEIGHBORHOOD_OVERRIDE_FIELDS = [
  'propertyPriceTrendMonthlyMedianPrice',
  'yoyMedianPricePercentage',
  'reoShortDistressedTrendMonthlyListings',
  'yoyReoListingsPercentage',
  'domTrend',
  'yoyDomPercentage',
];

const OVERRIDE_TOLERANCE = 0.005;//the inputs can round what DS sends, that must not count as an edit

function toNumber(value: any): number | null {
  if (!hasValue(value)) {
    return null;
  }
  const number = Number(typeof value === 'string' ? value.replace(/,/g, '') : value);
  return isNaN(number) ? null : number;
}

//Only what the analyst changed is saved, everything equal to DS stays null so DS keeps feeding it.
function toNumberOverride(value: any, dsValue: any): number | null {
  const number = toNumber(value);
  const dsNumber = toNumber(dsValue);
  if (number === null) {
    return null;
  }
  return dsNumber !== null && Math.abs(number - dsNumber) < OVERRIDE_TOLERANCE ? null : number;
}

function toDateOverride(value: any, dsValue: any): string | null {
  const date = toMMDDYYYY(value);
  return date && date !== toMMDDYYYY(dsValue) ? date : null;
}

export interface ReportImages {
  subjectImage?: string | null;
  //property comp id -> data URI
  compImages?: Record<number, string>;
}

export class InternalValuationReviewStore extends FormStore {
  globalStore: GlobalStore;
  subjectPropertyInformationStore: SubjectPropertyInformationStore;
  valuationFormStore: ValuationFormStore;
  neighborhoodFormStore: NeighborhoodFormStore;
  propertyCompFormStores: PropertyCompFormStore[];
  loanId: number;
  propertyId: number;
  readOnly: boolean;
  initialData: any;
  reportHistoryRecords: any[];
  loanDocument: any;
  //true while the report pages are being measured and laid out, the report must not be captured before that
  reportLayoutPending = false;
  //photos of the report as data URIs, loaded when the report is generated and dropped afterwards (never saved)
  reportImages: ReportImages | null = null;
  //path of the photo of the subject uploaded from the VTR form (only one), it wins over the DS photos
  subjectImage: string | null = null;
  //the uploaded photo downloaded as an object URL, so the form and the report can show it
  uploadedSubjectImageObjectUrl: string | null = null;

  internalValuationReviewService: InternalValuationReviewService;
  valuationReviewInternalService: ValuationReviewReportInternalService;
  private documentService: DocumentService;

  constructor(globalStore: GlobalStore) {
    super(form, globalStore);
    this.globalStore = globalStore;
    this.subjectPropertyInformationStore = new SubjectPropertyInformationStore(globalStore);
    this.valuationFormStore = new ValuationFormStore(globalStore);
    this.neighborhoodFormStore = new NeighborhoodFormStore(globalStore);

    this.internalValuationReviewService = new InternalValuationReviewService();
    this.valuationReviewInternalService = new ValuationReviewReportInternalService();
    this.documentService = new DocumentService();
    this.setDefaults();

    makeObservable(this, {
      loadInternalValuationReviewData: flow,
      loanId: observable,
      propertyId: observable,
      readOnly: observable,
      saveInternalValuationReview: flow,
      uploadPropertyCompImage: flow,
      reportLayoutPending: observable,
      setReportLayoutPending: action,
      reportImages: observable.ref,
      setReportImages: action,
      subjectImage: observable,
      uploadedSubjectImageObjectUrl: observable,
      setUploadedSubjectImageObjectUrl: action,
      uploadSubjectImage: flow,
      subjectCoverImageUrl: computed,
      downloadAppraisalReport: flow,
      getInternalValuationReviewData: action,
      isDirty: computed,
      loadStore: action,
      onBestComparableChange: action,
      propertyCompFormStores: observable,
      reportHistoryRecords: observable,
      loadReportHistoryRecords: action,
      uploadInternalValuationReportDocument: flow,
      renovationBudget: computed,
      completedRenovation: computed,
      totalRenovationBudget: computed,
      projectSummary: computed,
      saveLegacyValuationDetails: flow,
      hasScopeOfWork: computed,
      isInternalPortal: computed,
      uploadRealValReportDocument: flow
    });
  }



  *loadInternalValuationReviewData(loanId: number, propertyId: number, readOnly: boolean, disableGlobalLoading = false) {
    try {
      this.loanId = loanId;
      this.propertyId = propertyId;


      const response: ApiResponse = yield this.internalValuationReviewService.getInternalValuationReviewData(loanId, propertyId, disableGlobalLoading);
      this.initialData = { ...response.data.data, charts: JSON.parse(response.data.data.charts) };

      this.readOnly = readOnly || this.initialData.readOnly;

      this.loadStore(this.initialData);
      if (this.initialData?.dsErrorMessage) {
        console.log("DS error message:", this.initialData?.dsErrorMessage);
        this.globalStore.notificationStore.showWarningNotification({
          message: "We couldn't find some property details for this address.",
        });
      }

      this.loadReportHistoryRecords(true);
    } catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  setDefaults() {
    this.reset();
    this.initialData = null;
    this.loanId = null;
    this.propertyId = null;
    this.readOnly = null;
    this.subjectPropertyInformationStore.reset();
    this.valuationFormStore.reset();
    this.neighborhoodFormStore.reset();
    this.propertyCompFormStores = [];
    this.reportHistoryRecords = [];
    this.loanDocument = null;
    this.reportImages = null;
    this.subjectImage = null;
    this.uploadedSubjectImageObjectUrl = null;
  }

  //DS values with the analyst's saved overrides on top (the report uses it too)
  getPropertyStatusInformation(payload = this.initialData) {
    const dsStatus = payload?.propertyStatusInformation;
    const saved = payload?.internalValuationReview;
    if (!dsStatus) {
      return dsStatus;
    }
    return {
      ...dsStatus,
      lastSaleDate: toMMDDYYYY(hasValue(saved?.lastSaleDate) ? saved.lastSaleDate : dsStatus.lastSaleDate),
      lastSalePrice: hasValue(saved?.lastSalePrice) ? saved.lastSalePrice : dsStatus.lastSalePrice,
    };
  }

  //DS values with the analyst's saved overrides on top (the report uses it too)
  getNeighborhoodHousingConditions(payload = this.initialData) {
    const dsNeighborhood = payload?.neighborhoodHousingConditions;
    const saved = payload?.internalValuationReview;
    if (!dsNeighborhood) {
      return dsNeighborhood;
    }
    const neighborhood = { ...dsNeighborhood };
    NEIGHBORHOOD_OVERRIDE_FIELDS.forEach(field => {
      if (hasValue(saved?.[field])) {
        neighborhood[field] = saved[field];
      }
    });
    return neighborhood;
  }

  loadStore(payload) {
    this.subjectImage = payload?.internalValuationReview?.subjectImage || null;
    this.loadUploadedSubjectImage();
    this.subjectPropertyInformationStore.loadStore({ ...payload, propertyStatusInformation: this.getPropertyStatusInformation(payload) });
    this.valuationFormStore.loadForm({ ...payload?.internalValuationReview, borrowerValue: payload?.borrowerValue, appraiser: payload?.appraiser });
    this.neighborhoodFormStore.loadForm({
      ...payload?.internalValuationReview,
      ...this.getNeighborhoodHousingConditions(payload),
    });

    const propertyComps = [...payload.propertyComps];
    this.propertyCompFormStores = [];
    if (propertyComps && propertyComps.length > 0) {
      propertyComps.sort((pc1, pc2) => pc1.sequence - pc2.sequence);
      propertyComps.forEach(propertyComp => {
        const propertyCompFormStore = new PropertyCompFormStore(this.globalStore);
        if (propertyComp.sequence === 0) {
          propertyComp.beds = payload?.internalValuationReview?.beds;
          propertyComp.baths = payload?.internalValuationReview?.baths;
          propertyComp.propertyType = payload?.internalValuationReview?.propertyType;
          propertyComp.yearBuilt = payload?.internalValuationReview?.yearBuilt;
        }

        propertyCompFormStore.loadForm(propertyComp);
        propertyCompFormStore.setCanUploadImage(propertyComp);
        this.propertyCompFormStores.push(propertyCompFormStore);
      })
    }
  }

  *saveInternalValuationReview(disableGlobalLoading = false) {
    try {
      //save internal valuation review
      const payload = this.getInternalValuationReviewData();
      const response = yield this.internalValuationReviewService.saveInternalValuationReview(payload, disableGlobalLoading);
      this.initialData.internalValuationReview = { ...response.data.data };

      //save legacy valuation review: this is temporary
      //done in background
      this.saveLegacyValuationDetails(payload, true);

      //save property comps
      let propertyComps = [];
      const propertyCompsPayload = this.getPropertyCompsData();

      if (propertyCompsPayload) {
        const propertyCompsResponse = yield this.internalValuationReviewService.updatePropertyComps(propertyCompsPayload, disableGlobalLoading);
        propertyComps = propertyCompsResponse.data.data;
      }

      this.loadStore({ ...this.initialData, propertyComps });
      if (!disableGlobalLoading) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: "Record Saved Successfully!",
        });
      }
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  setReportLayoutPending(value: boolean) {
    this.reportLayoutPending = value;
  }

  //uploads the photo of a comparable that came without one, it is saved right away (it does not wait for Save)
  *uploadPropertyCompImage(propertyCompFormStore: PropertyCompFormStore, file: File) {
    try {
      const propertyCompId = propertyCompFormStore.form.fields.propertyCompId.value;
      const response = yield this.internalValuationReviewService.uploadPropertyCompImage(this.loanId, propertyCompId, file);
      if (response?.data?.status !== 'OK' || !response?.data?.data?.propertyImage) {
        this.globalStore.notificationStore.showErrorNotification({
          message: response?.data?.error?.message || "There was an error uploading the photo.",
        });
        return false;
      }
      propertyCompFormStore.setUploadedImage(response.data.data.propertyImage);
      this.globalStore.notificationStore.showSuccessNotification({
        message: "Photo uploaded successfully.",
      });
      return true;
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: "There was an error uploading the photo.",
      });
      return false;
    }
  }

  //Uploads the photo of the subject (only one). The review has to exist to keep the path, so it is saved first when it is new.
  *uploadSubjectImage(file: File) {
    try {
      if (!this.initialData?.internalValuationReview?.internalValuationReviewId) {
        yield this.saveInternalValuationReview(true);
      }
      if (!this.initialData?.internalValuationReview?.internalValuationReviewId) {
        return false;
      }
      const response = yield this.internalValuationReviewService.uploadSubjectImage(this.loanId, this.propertyId, file);
      const subjectImage = response?.data?.data?.subjectImage;
      if (response?.data?.status !== 'OK' || !subjectImage) {
        this.globalStore.notificationStore.showErrorNotification({
          message: response?.data?.error?.message || "There was an error uploading the photo.",
        });
        return false;
      }
      this.initialData.internalValuationReview = { ...this.initialData.internalValuationReview, subjectImage };
      this.subjectImage = subjectImage;
      this.loadUploadedSubjectImage();
      this.globalStore.notificationStore.showSuccessNotification({
        message: "Photo uploaded successfully.",
      });
      return true;
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: "There was an error uploading the photo.",
      });
      return false;
    }
  }

  setUploadedSubjectImageObjectUrl(url: string | null) {
    this.uploadedSubjectImageObjectUrl = url;
  }

  //downloads the uploaded photo (with the user session) so it can be shown in the form and in the report
  async loadUploadedSubjectImage() {
    const path = this.subjectImage;
    if (!path) {
      this.setUploadedSubjectImageObjectUrl(null);
      return;
    }
    const url = await loadSubjectImage(this.loanId, this.propertyId, path);
    if (this.subjectImage === path) {
      this.setUploadedSubjectImageObjectUrl(url);
    }
  }

  //the uploaded photo as an object URL, once it was downloaded
  get uploadedSubjectImageUrl(): string | null {
    return this.uploadedSubjectImageObjectUrl || (this.subjectImage ? (getCachedPropertyCompImage(this.subjectImage) || null) : null);
  }

  setReportImages(reportImages: ReportImages | null) {
    this.reportImages = reportImages;
  }

  //the cover of the report: the photo DS recommends, or the first one
  get subjectCoverImageUrl(): string | null {
    const images = this.initialData?.propertyImages;
    return images?.find(image => image.recommendedPreview === "true")?.url || images?.[0]?.url || null;
  }

  //The report PDF is drawn in the browser and a photo from a site without CORS headers (e.g. a signed CDN URL) comes out blank.
  //So the server downloads them all in a single call and sends them as data URIs (it decides where each photo comes from, no URL is sent).
  //If that fails the report uses the URLs as before.
  async loadReportImages() {
    try {
      const response: any = await this.internalValuationReviewService.getReportImages(this.loanId, this.propertyId);
      const images = response?.data?.data;
      this.setReportImages(response?.data?.status === 'OK' && images ? images : null);
    } catch (e) {
      console.log(e);
      this.setReportImages(null);
    }
  }

  //the report is captured right after it is rendered, so the uploaded photos have to be downloaded before that
  async preloadPropertyCompImages() {
    if (this.subjectImage) {
      await loadSubjectImage(this.loanId, this.propertyId, this.subjectImage);
    }
    await Promise.all((this.propertyCompFormStores || []).map(store => {
      const image = store.form.fields.propertyImage.value;
      if (!image || isRemoteImage(image)) {
        return null;
      }
      return loadPropertyCompImage(store.form.fields.propertyCompId.value, image);
    }));
  }

  getInternalValuationReviewData() {
    const propertyInformationValues: any = this.subjectPropertyInformationStore.propertyInformationFormStore.getFlattenedValues();
    const valuationValues: any = this.valuationFormStore.getFlattenedValues();
    const neighborhoodValues: any = this.neighborhoodFormStore.getFlattenedValues();
    const statusValues: any = this.subjectPropertyInformationStore.propertyStatusInformationStore.getFlattenedValues();
    const dsStatus = this.initialData?.propertyStatusInformation;
    const dsNeighborhood = this.initialData?.neighborhoodHousingConditions;

    const internalValuationReview = {
      ...this.initialData.internalValuationReview,
      ...propertyInformationValues,
      beds: convertStringNumberToNumber(propertyInformationValues.beds),
      baths: convertStringNumberToNumber(propertyInformationValues.baths),
      livingArea: convertStringNumberToNumber(propertyInformationValues.livingArea),
      landArea: convertStringNumberToNumber(propertyInformationValues.landArea),
      parking: convertStringNumberToNumber(propertyInformationValues.parking),
      yearBuilt: convertStringNumberToNumber(propertyInformationValues.yearBuilt),
      asRepairedValue: valuationValues.asRepairedValue,
      asIsValue: valuationValues.asIsValue,
      siteValue: valuationValues.siteValue,
      valueReconciliationComments: valuationValues.valueReconciliationComments,
      location: neighborhoodValues.location,
      neighborhoodZoning: neighborhoodValues.neighborhoodZoning,
      highestAndBestUse: neighborhoodValues.highestAndBestUse,
      hasAdverseFactors: neighborhoodValues.hasAdverseFactors,
      adverseFactorsComments: neighborhoodValues.adverseFactorsComments,
      lastSaleDate: toDateOverride(statusValues.lastSaleDate, dsStatus?.lastSaleDate),
      lastSalePrice: toNumberOverride(statusValues.lastSalePrice, dsStatus?.lastSalePrice),
      propertyPriceTrendMonthlyMedianPrice: toNumberOverride(neighborhoodValues.propertyPriceTrendMonthlyMedianPrice, dsNeighborhood?.propertyPriceTrendMonthlyMedianPrice),
      yoyMedianPricePercentage: toNumberOverride(neighborhoodValues.yoyMedianPricePercentage, dsNeighborhood?.yoyMedianPricePercentage),
      reoShortDistressedTrendMonthlyListings: toNumberOverride(neighborhoodValues.reoShortDistressedTrendMonthlyListings, dsNeighborhood?.reoShortDistressedTrendMonthlyListings),
      yoyReoListingsPercentage: toNumberOverride(neighborhoodValues.yoyReoListingsPercentage, dsNeighborhood?.yoyReoListingsPercentage),
      domTrend: toNumberOverride(neighborhoodValues.domTrend, dsNeighborhood?.domTrend),
      yoyDomPercentage: toNumberOverride(neighborhoodValues.yoyDomPercentage, dsNeighborhood?.yoyDomPercentage),
    };
    return internalValuationReview;
  }

  getPropertyCompsData() {
    if (!this.propertyCompFormStores || this.propertyCompFormStores.length === 0) {
      return null;
    }

    return this.propertyCompFormStores.map(store => {
      const flattenedValues: any = store.getFlattenedValues();
      return {
        ...flattenedValues,
        originalListDate: formatDate(flattenedValues.originalListDate, DateFormat.MMDDYYYY),
        saleDate: formatDate(flattenedValues.saleDate, DateFormat.MMDDYYYY),
      }
    }
    );
  }

  *downloadAppraisalReport() {
    try {
      yield this.valuationReviewInternalService.downloadAppraisalReport(this.propertyId);
    } catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  get isDirty() {
    let propertyCompsDirty = false;
    this.propertyCompFormStores.forEach(store => {
      propertyCompsDirty = propertyCompsDirty || store.isDirty;
    });

    return this.subjectPropertyInformationStore.propertyInformationFormStore.isDirty
      || this.subjectPropertyInformationStore.propertyStatusInformationStore.isDirty
      || this.valuationFormStore.isDirty
      || this.neighborhoodFormStore.isDirty
      || propertyCompsDirty;
  }

  onBestComparableChange(propertyCompId: number, value: any) {
    const booleanValue = value === 'true';

    const changedStore = this.propertyCompFormStores.find(store => store.form.fields.propertyCompId.value === propertyCompId);
    const othersStores = this.propertyCompFormStores.filter(store => store.form.fields.propertyCompId.value !== propertyCompId);

    changedStore.onFieldChange("bestComparable", booleanValue);
    othersStores.forEach(store => store.onFieldChange("bestComparable", !booleanValue));
  }

  getPropertyCompsMarkers() {
    const compForms: any[] = this.propertyCompFormStores.map(store => store.getFlattenedValues());

    const markers = [];
    if (compForms && compForms.length > 0) {
      compForms.forEach((propertyComp, index) => {
        let proximity = formatNumberField(propertyComp.proximity, 2, 2);
        if (proximity !== '-') {
          proximity += ' miles';
        }

        markers.push({
          id: propertyComp.propertyCompId,
          mapTitle: index === 0 ? "Subject" : `C${index}`,
          isSubjectProperty: index === 0,
          lat: propertyComp.latitude,
          lng: propertyComp.longitude,
          description: index === 0 ? "Subject Property" : `Comparable Property #${index}`,
          address: getFullAddress(propertyComp),
          proximity: proximity
        })
      })

    }
    return markers;
  }

  loadReportHistoryRecords(disableGlobalLoading = false) {
    if (this.initialData?.loanTaskId) {
      this.documentService.getDocumentHistoryWithUserFullName(this.initialData?.loanTaskId, disableGlobalLoading)
        .then(response => {
          if (response?.data?.data) {
            this.reportHistoryRecords = response.data.data;
          }
        }).catch(err => {
          console.log(err);
          this.globalStore.notificationStore.showErrorNotification({
            message: "There was an error fetching the Report History Records.",
          });
        });
    }
  }

  *uploadInternalValuationReportDocument(file: File) {
    if (!this.initialData.loanTaskId) {
      return;
    }

    const blob = file.slice(0, file.size, file.type);
    const newFile = new File([blob], file.name, { type: file.type });
    const formData = new FormData();
    formData.append('file', newFile);

    const response = yield this.documentService.uploadInternalValuationReport(
      this.loanId,
      this.propertyId,
      this.initialData.loanTaskId,
      formData,
      true
    );
    this.globalStore.notificationStore.showSuccessNotification({
      message: "Report uploaded successfully.",
    });

    this.loadReportHistoryRecords(true);
    return response;
  }

  *uploadRealValReportDocument(file: File) {
    const blob = file.slice(0, file.size, file.type);
    const newFile = new File([blob], file.name, { type: file.type });
    const formData = new FormData();
    formData.append('file', newFile);

    const response = yield this.documentService.uploadRealValReport(
      this.loanId,
      this.propertyId,
      formData,
      true
    );
    return response;
  }

  get hasScopeOfWork() {
    return !!this.initialData?.scopeOfWork;
  }

  get renovationBudget(): number {
    if (!this.initialData?.scopeOfWork) {
      return null;
    }
    return remainingReno(this.initialData.scopeOfWork.categoryMap);
  }
  get completedRenovation(): number {
    if (!this.initialData?.scopeOfWork) {
      return null;
    }
    return totalCapex(this.initialData.scopeOfWork.categoryMap);
  }
  get totalRenovationBudget(): number {
    if (!this.initialData?.scopeOfWork) {
      return null;
    }
    return totalRemainingReno(this.initialData.scopeOfWork.categoryMap);
  }
  get projectSummary() {
    if (!this.initialData?.scopeOfWork) {
      return null;
    }
    return this.initialData.scopeOfWork.projectDescription;
  }

  async getPropertyLoanDocument(loanId: number, collateralId: number) {
    try {
      const response = await this.documentService.getPropertyDocument(loanId, "Valuation Team Report", collateralId);

      if (response?.data?.status === 'OK' && response?.data?.data) {
        return await JSON.parse(response?.data?.data);
      }
      return null;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  *saveLegacyValuationDetails(payload, disableGlobalLoading) {
    try {

      const internalValue = [LoanSubType.FIX_AND_FLIP, LoanSubType.FIX_AND_FLIP_PRO, LoanSubType.GROUND_UP].includes(this.initialData.loanSubType) ? payload.asRepairedValue : payload.asIsValue;

      const data = {
        propertyId: this.propertyId,
        loanId: this.loanId,
        comments: payload.valueReconciliationComments,
        internalValue
      }
      yield this.valuationReviewInternalService.saveValuationDetails(data, disableGlobalLoading);
    }
    catch (error) {
      console.log(error);
    }
  }

  showScopeOfWork() {
    if (!this.initialData?.loanSubType) {
      return false;
    }
    return [LoanSubType.FIX_AND_FLIP, LoanSubType.FIX_AND_FLIP_PRO, LoanSubType.GROUND_UP].includes(this.initialData.loanSubType);
  }

  getCompToolUrl() {
    let parentHostName = "http://localhost:3000/";
    if (!isLocalHost()) {
      try {
        parentHostName = window.parent.location.origin + '/nportal';
      } catch (e) {
        console.error("Cannot access parent window origin:", e);
      }
    }
    return `${parentHostName}#/comp-tool/${encodeURIComponent(this.initialData.propertyFullAddress)}?loanId=${this.loanId}&propertyId=${this.propertyId}`;
  }

  get isInternalPortal() {
    return this.globalStore.isInternalPortal;
  }

}
